import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import { ACL, FIRST_VISIBLE_ROUTES } from 'appenv';
import { fetchXtraEvent } from 'models/cms/events/reducers';
import { getLocation } from 'models/location';
import { localizationSlice } from 'models/localization';
import connectSingleSessionService from 'network/singleUserSessionClient';
import registerEventxtraAttendee from 'network/registerEventxtraAttendeeAsync';
import { EventDatetimeContext } from 'components/EventDatetime';
import { getCMSRequests, setBearerTokenForCMSRequest } from 'network/cmsRequests';
import {
  GraphQLAttendee, GraphQLFetchCanPerformOnEventQuery, GraphQLRegisterEventxtraAttendeeResult,
} from 'network/graphql-models';
import AuthenticatingIndicator from './AuthenticatingIndicator';

function fetchUserSession(): UserSession {
  try {
    return JSON.parse(localStorage.getItem('userSession')) || {};
  } catch (e) {
    return {};
  }
}

export type UserSession = {
  authToken?: string;
  accessToken?: string;
  fullName?: string;
  lastReceivedAt?: moment.Moment;
  eventPermission?: Partial<(GraphQLFetchCanPerformOnEventQuery['data'] & { __typename: 'Event' })>;
  role?: string;
} & Partial<GraphQLAttendee>;

declare global {
  interface Window {
    trackingUser: (userSession: UserSession) => any;
    onlineStatus: (userSession: UserSession) => any;
  }
}

function saveUserSession(userSession: UserSession) {
  localStorage.setItem('userSession', JSON.stringify(userSession));
  window.trackingEventToFirebase(userSession, 'Authorization', '', '');
  window.onlineStatus(userSession);
}

const UserSessionPersistentStore = {
  fetch: fetchUserSession,
  save: saveUserSession,
};

const UserSessionContext = React.createContext<any>({
  userSession: {},
  setUserSession(userSession) {
  },
  canSee: (item) => false,
  refreshUserSession: () => {
  },
  firstVisibleRoute: null,
  isAuthenticating: false,
  isAuthenticated: false,
  canPerformVisitEventUnrestricted: false,
});

function UserSessionContextStateProvider({ children }) {
  const [userSessionState, setUserSessionState] = useState<UserSession>(UserSessionPersistentStore.fetch());
  const dispatch = useDispatch();
  const [authToken, setAuthToken] = useState((
    queryString.parse(document.location.search).auth_token
    || queryString.parse(document.location.search)['auth§token']
  ) as string | null);
  const { hasWebinarGoneLive, hasEventEnded, hasEventStarted } = useContext(EventDatetimeContext);
  const { features, chatroomConfig } = useSelector((s: any) => s.eventConfigs);
  const isAuthenticating = authToken && authToken !== '';
  const isAuthenticated = !isEmpty(userSessionState);
  const canPerformVisitUnrestricted = userSessionState?.eventPermission?.canPerformVisitUnrestricted || features.includes('internalQA');

  function canSee(item) {
    if (item === 'contacts' && (isChatroomDisabled(chatroomConfig) || chatroomConfig?.isContactCardDisabled)) {
      return false;
    }

    if (item === 'boothPreview') {
      return features.includes('boothPreview');
    }

    if (!canPerformVisitUnrestricted && (!hasEventStarted || hasEventEnded)) {
      if (hasEventEnded) {
        localStorage.clear();
      }
      // User can only see landing page if event hasn't started
      return ACL({}, item);
    }

    if (item === 'hall' && features.includes('hideHall')) {
      return false;
    }

    if (item === 'webinar' && (!hasWebinarGoneLive || features.includes('hideWebinar'))) {
      // User can't see webinar page unless it goes live
      return false;
    }

    if (item === 'participant') {
      return ACL(userSessionState || {}, item) && !features.includes('hideParticipantPage');
    }

    if (item === 'products' && !features.includes('productPage')) {
      return false;
    }

    if (item === 'publicChatroom' && !features.includes('publicChatroom')) {
      return false;
    }

    if (item === 'speakers' && !features.includes('speakersPage')) {
      return false;
    }

    return ACL(userSessionState || {}, item);
  }

  const getUserRole = (vexpoOrganizer, vexpoRole) => {
    if (vexpoOrganizer) return 'organizer';
    switch (vexpoRole?.toLowerCase()) {
      case 'organizer':
      case 'exhibitor':
        return vexpoRole?.toLowerCase();
      default:
        return 'attendee';
    }
  };

  async function refreshUserSession(eventId) {
    let userSession = UserSessionPersistentStore.fetch() || {};
    const resolvedAuthToken = authToken || userSession.authToken;
    if (!resolvedAuthToken) return;
    try {
      const response = (await registerEventxtraAttendee(eventId, resolvedAuthToken)) as GraphQLRegisterEventxtraAttendeeResult;
      const attendeeData = response?.attendee;
      if (attendeeData) {
        const {
          customAttendeeFields: {
            vexpo_organizer: vexpoOrganizer,
            vexpo_role: vexpoRole,
          },
        } = attendeeData;

        setBearerTokenForCMSRequest(response.accessToken);
        const permission = (await getCMSRequests().fetchCanPerformOnEvent({ id: eventId }))?.data;
        const eventPermission = permission as (typeof permission & { __typename: 'Event' });

        if (features.includes('singleUserSession')) {
          connectSingleSessionService(resolvedAuthToken);
        }

        userSession = {
          authToken: resolvedAuthToken,
          accessToken: response.accessToken,
          role: getUserRole(vexpoOrganizer, vexpoRole),
          ...attendeeData,
          eventPermission,
          fullName: `${attendeeData.firstName} ${attendeeData.lastName}`,
          lastReceivedAt: moment(),
        };
      } else {
        userSession = {};
      }
    } catch (e) {
      console.error(e);
    }
    UserSessionPersistentStore.save(userSession);
  }

  function loadFromPersistentStore() {
    const userSession = UserSessionPersistentStore.fetch();
    setUserSessionState(userSession);
    setAuthToken(null);
  }

  async function initialize(eventId) {
    await refreshUserSession(eventId);
    loadFromPersistentStore();
  }

  useEffect(() => setBearerTokenForCMSRequest(userSessionState?.accessToken), [userSessionState?.accessToken]);

  useEffect(() => {
    dispatch(fetchXtraEvent()).then(async ({ payload }) => {
      const eventId = Object.keys(payload)[0];
      await dispatch(localizationSlice.actions.setAvailableLocales(payload[eventId].locales));
      await dispatch(localizationSlice.actions.setLocaleByDetection());
      initialize(eventId);
    });
    dispatch(getLocation());
  }, []);

  const setUserSession = useCallback((newUserSession) => {
    UserSessionPersistentStore.save(newUserSession);
    loadFromPersistentStore();
  }, []);

  const firstVisibleRoute = FIRST_VISIBLE_ROUTES.find((route) => canSee(route));

  if (isAuthenticating) {
    return <AuthenticatingIndicator />;
  }

  return (
    <UserSessionContext.Provider
      value={{
        userSession: userSessionState,
        setUserSession,
        canSee,
        canPerformVisitEventUnrestricted: canPerformVisitUnrestricted,
        isAuthenticating,
        isAuthenticated,
        firstVisibleRoute,
      }}
    >
      {children}
    </UserSessionContext.Provider>
  );
}

UserSessionContextStateProvider.propTypes = {
  children: PropTypes.node,
};

UserSessionContextStateProvider.defaultProps = {
  children: null,
};

export {
  UserSessionContext,
  UserSessionContextStateProvider,
  UserSessionPersistentStore,
};
